import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { fonts, colors } from '../../styles/theme';
import { formatCurrency } from '../../utils/formatters';
import InfoTooltip from '../common/overlays/InfoTooltip';

// Hintergrundfarben für Bauphase (Minus-Jahre)
const preOperationColors = {
  header: colors.background.tableHeaderPreOp,  // Etwas dunkler für Header
  cell: colors.background.tableHeaderAlt,      // Subtile Unterscheidung für Zellen
};

/**
 * Definition einer Zeile in der HorizontalTable
 */
export interface HorizontalTableRow {
  /** Eindeutiger Schlüssel für die Zeile */
  key: string;
  /** Label in der ersten Spalte */
  label: string;
  /** Werte für jede Datenspalte */
  values: (string | number | null | undefined)[];
  /** Optionaler Wert für die letzte Spalte (z.B. Summe) */
  lastColumnValue?: string | number | null;
  /** Zeile optisch hervorheben (z.B. für Zwischensummen) */
  highlightRow?: boolean;
  /** Funktion um Textfarbe zu bestimmen (erhält den Wert, gibt CSS-Farbe zurück) */
  valueColor?: (value: number) => string | undefined;
  /** Funktion um einen farbigen Balken am unteren Zellenrand + Textfarbe zu bestimmen (erhält den Wert, gibt CSS-Farbe zurück) */
  colorBar?: (value: number) => string | undefined;
  /** Optionaler Tooltip-Text für das Label (z.B. Berechnungsformel) */
  tooltip?: string;
}

/**
 * Definition der optionalen letzten Spalte
 */
export interface LastColumnConfig {
  /** Header-Text (z.B. "Summe") */
  header: string;
}

/**
 * Props für die HorizontalTable-Komponente
 */
export interface HorizontalTableProps {
  /** Header-Label für die erste Spalte (z.B. "Jahr", kann leer sein) */
  headerLabel?: string;
  /** Spaltenüberschriften (z.B. Jahre: [1, 2, 3, ...]) */
  columnHeaders: (string | number)[];
  /** Zeilen-Daten */
  rows: HorizontalTableRow[];
  /** Optionale Konfiguration für die letzte Spalte */
  lastColumn?: LastColumnConfig;
  /** Custom Formatter für Werte (Standard: formatCurrency) */
  formatValue?: (value: string | number | null | undefined) => string;
  /** Minimale Breite der ersten Spalte in px (Standard: 120) */
  labelColumnWidth?: number;
  /** Minimale Breite der Datenspalten in px (Standard: 100) */
  dataColumnWidth?: number;
  /** Custom Cell Renderer (optional) - ermöglicht z.B. editierbare Zellen */
  renderCell?: (rowKey: string, columnHeader: string | number, value: string | number | null | undefined) => React.ReactNode;
}

/**
 * HorizontalTable - Wiederverwendbare Tabelle mit horizontaler Ausrichtung
 * 
 * Features:
 * - Sticky erste Spalte mit Labels
 * - Dynamische Anzahl Spalten
 * - Optionale letzte Spalte (z.B. für Summen)
 * - Einheitliches Design (basierend auf RepaymentSection)
 * - Monospace-Font für Zahlen
 */
const HorizontalTable: React.FC<HorizontalTableProps> = ({
  headerLabel = '',
  columnHeaders,
  rows,
  lastColumn,
  formatValue = formatCurrency,
  labelColumnWidth = 120,
  dataColumnWidth = 100,
  renderCell,
}) => {
  /**
   * Formatiert einen Wert für die Anzeige
   */
  const formatDisplayValue = (value: string | number | null | undefined): string => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'string') return value;
    return formatValue(value);
  };

  return (
    <TableContainer
      sx={{
        borderRadius: 2, // Abgerundete Ecken wie bei BaseTable
        overflowX: 'auto', // Horizontales Scrollen ermöglichen
        overflowY: 'visible',
      }}
    >
      <Table
        size="small"
        sx={{
          minWidth: 'max-content',
          borderCollapse: 'separate',
          borderSpacing: 0,
          '& .MuiTableCell-root': { py: 1.8 },
        }}
      >
        <TableHead>
          <TableRow sx={{ backgroundColor: colors.background.tableHeader }}>
            {/* Erste Spalte: Header-Label */}
            <TableCell
              sx={{
                fontWeight: fonts.weights.semiBold,
                width: labelColumnWidth,
                minWidth: labelColumnWidth,
                maxWidth: labelColumnWidth,
                position: 'sticky',
                left: 0,
                backgroundColor: colors.background.tableHeader,
                zIndex: 1,
                borderRight: `1px solid ${grey[300]}`,
              }}
            >
              {headerLabel}
            </TableCell>

            {/* Dynamische Spaltenüberschriften */}
            {columnHeaders.map((header, index) => {
              const isPreOperation = typeof header === 'number' && header < 1;
              const headerContent = (
                <TableCell
                  key={`header-${index}`}
                  align="right"
                  sx={{
                    fontWeight: fonts.weights.semiBold,
                    width: dataColumnWidth,
                    minWidth: dataColumnWidth,
                    maxWidth: dataColumnWidth,
                    whiteSpace: 'nowrap',
                    backgroundColor: isPreOperation ? preOperationColors.header : undefined,
                  }}
                >
                  {header}
                </TableCell>
              );

              // Tooltip für Minus-Jahre
              if (isPreOperation) {
                return (
                  <Tooltip
                    key={`header-${index}`}
                    title="Jahr vor Betriebsbeginn (Bau- und Finanzierungsphase)"
                    arrow
                  >
                    {headerContent}
                  </Tooltip>
                );
              }
              return headerContent;
            })}

            {/* Optionale letzte Spalte (z.B. Summe) */}
            {lastColumn && (
              <TableCell
                align="right"
                sx={{
                  fontWeight: fonts.weights.bold,
                  width: dataColumnWidth,
                  minWidth: dataColumnWidth,
                  maxWidth: dataColumnWidth,
                  backgroundColor: colors.background.tableHeader,
                  borderLeft: `2px solid ${grey[400]}`,
                }}
              >
                {lastColumn.header}
              </TableCell>
            )}
          </TableRow>
        </TableHead>

        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={row.key}
              sx={{
                backgroundColor: row.highlightRow ? grey[50] : 'inherit',
              }}
            >
              {/* Erste Spalte: Label mit optionalem Tooltip */}
              <TableCell
                sx={{
                  fontWeight: row.highlightRow ? fonts.weights.semiBold : fonts.weights.medium,
                  width: labelColumnWidth,
                  minWidth: labelColumnWidth,
                  maxWidth: labelColumnWidth,
                  position: 'sticky',
                  left: 0,
                  backgroundColor: row.highlightRow ? grey[50] : 'white',
                  zIndex: 1,
                  borderRight: `1px solid ${grey[300]}`,
                }}
              >
                {row.tooltip ? (
                  <InfoTooltip title={row.tooltip}>
                    {row.label}
                  </InfoTooltip>
                ) : (
                  row.label
                )}
              </TableCell>

              {/* Werte-Spalten */}
              {row.values.map((value, index) => {
                const numericValue = typeof value === 'number' ? value : 0;
                const customColor = row.valueColor ? row.valueColor(numericValue) : undefined;
                const barColor = row.colorBar ? row.colorBar(numericValue) : undefined;
                const columnHeader = columnHeaders[index];
                const isPreOperation = typeof columnHeader === 'number' && columnHeader < 1;

                const barHeight = row.key === 'dscr' ? 2 : 4;

                return (
                  <TableCell
                    key={`${row.key}-${index}`}
                    align="right"
                    sx={{
                      fontFamily: fonts.monospace,
                      fontWeight: row.highlightRow ? fonts.weights.medium : 'inherit',
                      width: dataColumnWidth,
                      minWidth: dataColumnWidth,
                      maxWidth: dataColumnWidth,
                      whiteSpace: 'nowrap',
                      color: customColor || barColor || 'inherit',
                      // Farbiger Balken am unteren Rand (ersetzt Zellborder)
                      borderBottom: barColor ? `${barHeight}px solid ${barColor}` : undefined,
                      // Hintergrundfarbe für Bauphase (Minus-Jahre)
                      backgroundColor: isPreOperation
                        ? (row.highlightRow ? preOperationColors.header : preOperationColors.cell)
                        : (row.highlightRow ? grey[50] : undefined),
                    }}
                  >
                    {renderCell
                      ? renderCell(row.key, columnHeader, value)
                      : formatDisplayValue(value)
                    }
                  </TableCell>
                );
              })}

              {/* Optionale letzte Spalte */}
              {lastColumn && (
                <TableCell
                  align="right"
                  sx={{
                    fontWeight: fonts.weights.bold,
                    fontFamily: fonts.monospace,
                    width: dataColumnWidth,
                    minWidth: dataColumnWidth,
                    maxWidth: dataColumnWidth,
                    backgroundColor: colors.background.tableHeader,
                    borderLeft: `2px solid ${grey[400]}`,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatDisplayValue(row.lastColumnValue)}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default HorizontalTable;
